import { useEffect } from "react";
import {
  callAPIInterface,
  displaySelectedCurrencyAmount,
  fetchPrecisedInteger,
  generateAmountToPass,
  getCurrencyAmount,
  minimumLightningUsdtNetFee,
  showAmount,
} from "../components/constants";
import { sats, usdc, usdt, XAUT } from "../components/messages";

function useEstimatedFees({
  amount,
  currency,
  data,
  speedFee,
  fixedFee,
  balance,
  showBtcAmount,
  isOnchainMethod,
  isLightningMethod,
  isLnUrlMethodSend,
  isLnUrlMethodReceive,
  isEthereumMethod,
  isTronMethod,
  isSolanaMethod,
  isTonMethod,
  isLightningStableCoin,
  setCalculatedSpeedFees,
  setNetworkFeeLoader,
  setNetworkFeeAmount,
  setConversionFeeAmount,
  setButtonLoader,
  isFromMiniApp = false,
}) {
  const getEstimatedFees = (value) => {
    const amt = generateAmountToPass(value, currency, isLightningStableCoin);
    if (amt > 0) {
      const estimationFeesData = {
        amount: amt,
        currency: currency,
        target_currency: currency,
        withdraw_request: data?.withdraw_request,
      };

      setNetworkFeeLoader(true);
      setButtonLoader?.(true);
      callAPIInterface("POST", "/send/estimate-fees", estimationFeesData)
        .then((res) => {
          let usdtLightningNetworkFee = res?.est_fees_in_currency;
          let networkFee = isLightningStableCoin
            ? fetchPrecisedInteger(usdtLightningNetworkFee, 2) === "0"
              ? minimumLightningUsdtNetFee
              : usdtLightningNetworkFee
            : getCurrencyAmount(res?.est_fees_in_target_currency, currency);

          const conversionFee = showAmount({
            amount: res?.est_conversion_fee_in_target_currency || 0,
            currency,
          });

          setNetworkFeeAmount(networkFee);
          setConversionFeeAmount(conversionFee);
          setNetworkFeeLoader(false);
          setButtonLoader?.(false);
        })
        .catch((err) => {
          setNetworkFeeLoader(false);
          setButtonLoader?.(false);
        });
    }
  };

  useEffect(() => {
    const value = Number(amount);
    const timeOut = setTimeout(() => {
      let satsValidAmount = false;
      let tapValidAmount = false;
      let validForMiniApp = isFromMiniApp && isLnUrlMethodReceive;

      const validTapAmountCondition =
        currency !== sats && value > 0 && value <= balance;

      if (isOnchainMethod) {
        const minRequiredAmount = displaySelectedCurrencyAmount(1000);
        satsValidAmount =
          currency === sats && value >= minRequiredAmount && value <= balance;
        tapValidAmount = validTapAmountCondition;
      } else if (isLightningMethod || isLnUrlMethodSend) {
        satsValidAmount =
          (currency === sats || currency === usdt || currency === usdc) &&
          value > 0 &&
          value <= balance;
      } else if (
        isEthereumMethod ||
        isTronMethod ||
        isSolanaMethod ||
        isTonMethod
      ) {
        tapValidAmount = validTapAmountCondition;
      }

      if (satsValidAmount || tapValidAmount) {
        let amountForEstimateFees = Number(amount);
        getEstimatedFees(amountForEstimateFees);
        if (amount) {
          const calculateFee = (amount * speedFee) / 100 + fixedFee;
          setCalculatedSpeedFees(
            currency === sats && !showBtcAmount
              ? Math.floor(calculateFee)
              : calculateFee?.toFixed(
                  showBtcAmount ? 8 : currency === XAUT ? 6 : 2
                )
          );
        }
      } else if (validForMiniApp && amount) {
        const calculateFee = (amount * speedFee) / 100 + fixedFee;

        setCalculatedSpeedFees(
          currency === sats && !showBtcAmount
            ? Math.floor(calculateFee)
            : calculateFee?.toFixed(showBtcAmount ? 8 : 2)
        );
        setNetworkFeeLoader(false);
      } else {
        setNetworkFeeLoader(false);
        setNetworkFeeAmount(0);
        setCalculatedSpeedFees(0);
      }
    }, 500);

    return () => clearTimeout(timeOut);
  }, [amount, balance, speedFee, currency]);
}

export default useEstimatedFees;
